/**
 * Austria (AT) withdrawal — asynchronous half
 * -------------------------------------------
 * Context and the non-negotiable scope rule live in
 * snippets/syba-at-restricted-product.liquid. Short version: ONE product
 * (SHIELD_01, "Nematodes Against Fungus Gnats", Steinernema feltiae) may not
 * be offered to Austrian visitors, Austria shares its Shopify market with
 * Germany, and anything here that catches a second product is a defect.
 *
 * The server-side Liquid gate already suppresses the product whenever the
 * country is known at render time. This file covers the three cases it
 * cannot:
 *
 *   1. The cold first pageview. assets/syba-geo-country-bridge.js writes the
 *      `_geo_country` cart attribute only AFTER the first render, so the very
 *      first page a new visitor sees is rendered with no country at all.
 *      (Same root cause as the IE fallback in the 2026-07-18 geo-orb review.)
 *   2. Cards the Globo filter app injects. The collection grid and the search
 *      grid are re-rendered client-side from snippets/globo.filter.product.liquid,
 *      so server Liquid never runs for a filtered or paginated result set.
 *   3. A country that changes during the session (the geo orb's picker fires
 *      `syba:geo-country`).
 *
 * The synchronous, no-network part of the check is inlined by
 * snippets/syba-at-restricted-assets.liquid so it can beat first paint. This
 * file picks up from there: if that check already decided, we only apply; if
 * it did not, we ask the edge.
 */
(function () {
  'use strict';
  if (window.__sybaAtRestrictLoaded) return;
  window.__sybaAtRestrictLoaded = true;

  var CLASS = 'syba-at-restricted';
  var HOOK = '[data-syba-geo-restrict="AT"]';
  // One card per cell everywhere in this theme (collection/search grids,
  // Splide sliders, the cart-drawer upsell list), so collapsing the cell is
  // safe. Guarded below anyway: we never collapse a cell that holds a second
  // card. Mirrors the :has() rules in the snippet, for browsers without :has.
  var CELL = 'li, .grid__item, .splide__slide, .swiper-slide';
  var observer = null;

  function isRestricted() {
    return document.documentElement.classList.contains(CLASS);
  }

  function hideCards() {
    if (!isRestricted()) return;
    var els = document.querySelectorAll(HOOK);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.getAttribute('data-syba-geo-hidden') === '1') continue;
      el.setAttribute('data-syba-geo-hidden', '1');
      el.setAttribute('aria-hidden', 'true');
      try { el.style.setProperty('display', 'none', 'important'); } catch (e) {}
      var cell = el.closest ? el.closest(CELL) : null;
      if (cell && cell !== el && cell.querySelectorAll('.card-wrapper, .card').length <= 1) {
        try { cell.style.setProperty('display', 'none', 'important'); } catch (e) {}
      }
    }
    // Fallback for a card that reached the DOM without the hook (a surface we
    // did not find, or a Globo template whose id comparison did not evaluate).
    // Matched on the product URL for the CURRENT locale, handed over by Liquid
    // so a translated handle on /de-de/ still matches.
    var path = window.SybaAtRestrictedPath;
    if (!path) return;
    var links = document.querySelectorAll('a[href*="' + String(path).replace(/"/g, '') + '"]');
    for (var j = 0; j < links.length; j++) {
      var card = links[j].closest ? links[j].closest('.card-wrapper, .card, ' + CELL) : null;
      if (!card || card.hasAttribute('data-syba-geo-hidden')) continue;
      // Never touch the PDP of ANOTHER product, a breadcrumb or a nav link:
      // only collapse something that is actually a product card.
      if (!card.classList.contains('card-wrapper') && !card.classList.contains('card') && !card.querySelector('.card-wrapper, .card')) continue;
      card.setAttribute('data-syba-geo-hidden', '1');
      try { card.style.setProperty('display', 'none', 'important'); } catch (e) {}
      var outer = card.closest ? card.closest(CELL) : null;
      if (outer && outer !== card && outer.querySelectorAll('.card-wrapper, .card').length <= 1) {
        try { outer.style.setProperty('display', 'none', 'important'); } catch (e) {}
      }
    }
  }

  function watch() {
    if (observer || !window.MutationObserver || !document.body) return;
    var pending = false;
    observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      // Coalesced: the Globo filter replaces the whole grid in one burst.
      requestAnimationFrame(function () { pending = false; hideCards(); });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function accept(iso) {
    if (String(iso || '').toUpperCase() !== 'AT') return false;
    document.documentElement.classList.add(CLASS);
    hideCards();
    watch();
    return true;
  }

  // The geo orb / bridge publishes the country the moment it has it.
  window.addEventListener('syba:geo-country', function (e) { accept(e && e.detail); });

  if (isRestricted()) {
    // The inline sync check already decided. Nothing left to look up.
    hideCards();
    watch();
    return;
  }

  // The bridge may have resolved the country in this same page load, after the
  // inline check ran but before this script did.
  if (accept(window.SybaDetectedCountry)) return;

  // Cold first pageview: nothing cached. Ask the edge ourselves rather than
  // wait for the bridge, which only starts on DOMContentLoaded. Same endpoint
  // and same parse as assets/syba-geo-country-bridge.js.
  fetch('/cdn-cgi/trace', { credentials: 'omit' })
    .then(function (r) { return r.text(); })
    .then(function (text) {
      var m = (text || '').match(/(?:^|\n)loc=([A-Z]{2})/);
      if (m) accept(m[1]);
    })
    .catch(function () { /* non-fatal: the next pageview is served by Liquid */ });
})();
